import { compareNames } from '@pubstack/common/src/sort'
import { FC, PropsWithChildren, ReactNode } from 'react'
import { Icon } from '../../components/Icon'
import { Skeleton } from '../../components/Skeleton'
import { WithClassName } from '../../types/utils'
import { TableCell } from './TableCell'
import { TableRow } from './TableRow'
import { thStyle } from '../_styled/table'
import { Colors } from '../../style/colors'

type SortOrder = 'ascending' | 'descending' | 'none'

export interface Column {
  name: ReactNode
  isSortable: false
}

export interface SortableColumn<T = any> {
  name: ReactNode
  isSortable: true
  order: SortOrder
  attributeSort: keyof T
  sort?: (a: T, b: T, order: SortOrder) => number
}

export type TableColumns<T = any> = (Column | SortableColumn<T>)[]

/* ---------------- SORT / SEARCH ---------------- */

export const handleTableSearchAndSort = <T extends { [key in keyof T]: object | string | number | boolean | null | undefined }>(
  columns: TableColumns<T>,
  data: T[],
  search: string,
  searchAttributes: (keyof T)[]
): T[] => {
  return handleTableSearch(handleTableSort(columns, data), search, searchAttributes)
}

// TODO tmu 2022-11-15 type correctly to disallow passing searchAttributes pointing to objects
/**
 * Return a filtered array containing values matching a search over specific stringable attributes
 * @param data array of objects to filter
 * @param search string value currently searched
 * @param searchAttributes object's properties on which we want to search
 */
export const handleTableSearch = <T extends { [key in keyof T]: object | string | number | boolean | null | undefined }>(data: T[], search: string, searchAttributes: (keyof T)[]): T[] => {
  return data.filter((d) => {
    return searchAttributes.some((attr) => {
      const value = d[attr as keyof T]
      if (value) {
        return value.toString().toLowerCase().includes(search.toLowerCase())
      }
    })
  })
}

export const onColumnSort = <T,>(columns: TableColumns<T>, column: SortableColumn<T>, setColumns: (columns: TableColumns<T>) => void) => {
  setColumns(
    columns.map((c) => {
      if (c === column) {
        return {
          ...c,
          order: c.order === 'ascending' ? 'descending' : 'ascending',
        }
      }
      return c.isSortable ? { ...c, order: 'none' } : c
    })
  )
}

const sortByType = (a: any, b: any): number => {
  switch (typeof a) {
    case 'number':
    case 'boolean':
      return Number(b) - Number(a)
    case 'string':
      // `String(b)`: `handleTableSort` hands over `false` for a missing value.
      return compareNames(a, String(b))
  }
  return 0
}

export const handleTableSort = <T,>(columns: TableColumns<T>, elements: T[] | undefined): T[] => {
  const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<T>

  return [...(elements || [])].sort((a, b) =>
    sortedColumn.sort
      ? sortedColumn.sort(a, b, sortedColumn.order) * (sortedColumn.order === 'ascending' ? 1 : -1)
      : (sortedColumn.order === 'ascending' ? 1 : -1) * sortByType(a[sortedColumn.attributeSort] ?? false, b[sortedColumn.attributeSort] ?? false)
  )
}

/* ---------------- TABLE COMPONENT ---------------- */

type TableProps<T = any> = WithClassName & {
  isLoading: boolean
  headersTop?: number
  columns: TableColumns<T>
  onClickHeading?: (column: SortableColumn) => void
  noWrapHeader?: boolean
}

const Table: FC<PropsWithChildren<TableProps>> = ({ children, headersTop = 0, className, noWrapHeader, ...props }) => {
  const seed = (s: number) => {
    return () => {
      s = Math.sin(s) * 10000
      return s - Math.floor(s)
    }
  }

  const random = seed(seed(seed(42)())())

  const headingClick = (column: Column | SortableColumn): void => {
    if (column.isSortable) {
      props.onClickHeading?.(column)
    }
  }

  const renderTh = (props.columns || []).map((column: Column | SortableColumn<any>, index) => {
    const sortable = column.isSortable

    const stickyStyle: React.CSSProperties = {
      position: 'sticky',
      top: headersTop,
      ...thStyle,
    }

    return (
      <th
        key={typeof column.name === 'string' && column.name ? `row-${column.name}` : `row-${index}`}
        onClick={() => headingClick(column)}
        style={stickyStyle}
        className={`last:border-b-0
        relative
        bg-white
        p-0
        ${sortable ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-center p-4 px-3 w-full font-normal text-slate-600">
          <span className="block w-full">{column.name}</span>

          {sortable && <Icon name={column.order === 'ascending' ? 'organize_up' : column.order === 'descending' ? 'organize_down' : 'organize'} size="xs" className="ml-0.5" />}
        </div>
      </th>
    )
  })

  const fakeColumns = (columns: TableColumns, rowIndex: number) =>
    columns.map((_, i) => (
      <TableCell key={`cell-${i}-${rowIndex}`}>
        <Skeleton width={`${Math.floor(random() * 31 + 40)}%`} />
      </TableCell>
    ))

  const bodyLoading = new Array(5).fill(0).map((_, i) => <TableRow key={`row-${i}`}>{fakeColumns(props.columns, i)}</TableRow>)

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        borderLeft: `1px solid ${Colors.Platinum}`,
        borderRight: `1px solid ${Colors.Platinum}`,
        borderRadius: '4px',
        overflow: 'clip',
      }}
      className={className ?? ''}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          backgroundColor: Colors.White,
          margin: 0,
          padding: 0,
          position: 'relative',
          color: Colors.Jet,
        }}
      >
        <thead
          style={{
            backgroundColor: Colors.White,
          }}
        >
          <tr
            style={{
              whiteSpace: noWrapHeader ? 'nowrap' : 'normal',
            }}
          >
            {renderTh}
          </tr>
        </thead>

        <tbody>{props.isLoading ? bodyLoading : children}</tbody>
      </table>
    </div>
  )
}

export default Table
